import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import Button from 'core/buttons/Button';
import isEmpty from 'helpers/isEmpty';
import { permissions } from 'app/data/permissions';
import { HasPermission } from 'hoc/withRole';
import { getFlashPaymentWebhookLogs } from 'app/features/flash-payment-webhook-logs/flashPaymentWebhookLogSaga';

const sx = {
  jsonValue: {
    mt: 0,
    p: 1.25,
    backgroundColor: '#f7f9fc',
    borderRadius: '0 0 6px 6px',
    fontFamily: 'monospace',
    fontSize: 12,
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    border: '1px solid',
    borderColor: 'divider',
    borderTop: 'none',
  },
  logToggleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    px: 1.5,
    py: 0.75,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': { backgroundColor: 'action.hover' },
  },
};

const formatJson = (value) => {
  if (value == null || value === '') return '-';
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return value;
  }
};

function JsonToggleField({ label, value, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box sx={{ mb: 1 }}>
      <Box component="button" type="button" onClick={() => setOpen(!open)} sx={[sx.logToggleButton]}>
        <Typography variant="body2" fontWeight={600} color="text.secondary" fontSize={12}>
          {label}
        </Typography>
        {open ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        )}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box component="pre" sx={sx.jsonValue}>
          {formatJson(value)}
        </Box>
      </Collapse>
    </Box>
  );
}

JsonToggleField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  defaultOpen: PropTypes.bool,
};

export default function TransactionWebhookLogsModal({ orderId }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useSelector((state) => state.flashPaymentWebhookLogs.index);

  useEffect(() => {
    if (open && orderId) {
      dispatch(
        getFlashPaymentWebhookLogs({
          OrderId: orderId,
          page_number: 1,
          page_size: 10,
        }),
      );
    }
  }, [open, orderId, dispatch]);

  if (!orderId) return null;

  return (
    <HasPermission permission={permissions.VIEW_FLASH_WEBHOOK_TRANSACTION_LOG}>
      <Button onClick={() => setOpen(true)}>Webhook Logs</Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh', maxWidth: '52vw' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            pr: 1,
          }}
        >
          <Typography component="span" variant="h6" fontWeight={600}>
            Webhook Log
          </Typography>
          <IconButton aria-label="close" onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : isEmpty(data) ? (
            <Typography variant="body2" color="text.secondary" py={2}>
              No webhook logs found for this transaction.
            </Typography>
          ) : (
            data.map((log, index) => {
              const isMultiple = data.length > 1;
              return (
                <Box key={log.id ?? index} sx={{ mb: isMultiple ? 2.5 : 0 }}>
                  {isMultiple && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" fontWeight={700}>
                        Log #{index + 1}
                      </Typography>
                      {log.transactionStatus && (
                        <Chip size="small" label={log.transactionStatus} />
                      )}
                      {log.flashPaymentId && (
                        <Typography variant="caption" color="text.secondary">
                          {log.flashPaymentId}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <JsonToggleField
                    label="Deposit Initiated Request"
                    value={log.depositInitiatedRequest}
                    defaultOpen={!isMultiple}
                  />
                  <JsonToggleField
                    label="Deposit Cleared Request"
                    value={log.depositClearedRequest}
                  />
                  {isMultiple && index < data.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              );
            })
          )}
        </DialogContent>
      </Dialog>
    </HasPermission>
  );
}

TransactionWebhookLogsModal.propTypes = {
  orderId: PropTypes.string,
};
